import { initiateCardPayment, checkPaymentAppAvailability } from './paymentUtils';
import { DEMO_MODE } from './demoMode';

export interface PaymentResult {
  success: boolean;
  message: string;
  error?: string;
  transactionId?: string;
}

export const processCardPayment = async (amount: number): Promise<PaymentResult> => {
  // v5 mode démo : pas de TPE branché → paiement simulé, toujours accepté
  if (DEMO_MODE) {
    console.log('[DEMO] Paiement carte simulé :', amount);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    return {
      success: true,
      message: 'Paiement effectué avec succès',
      transactionId: `DEMO-${Date.now()}`
    };
  }

  try {
    // First check if payment app is available
    const isAvailable = await checkPaymentAppAvailability();
    if (!isAvailable) {
      return {
        success: false,
        message: 'L\'application de paiement n\'est pas installée',
        error: 'app_not_installed'
      };
    }

    // Proceed with payment
    const result = await initiateCardPayment(amount, 'EUR');
    
    if (result.status === 'success') {
      return {
        success: true,
        message: 'Paiement effectué avec succès',
        transactionId: result.transactionId
      };
    } else {
      return {
        success: false,
        message: getErrorMessage(result.error || 'unknown_error'),
        error: result.error,
        transactionId: result.transactionId
      };
    }
  } catch (error) {
    if (error instanceof Error) {
      return {
        success: false,
        message: getErrorMessage(error.message),
        error: error.message
      };
    }
    
    // Handle case where error is a PaymentResult
    if (typeof error === 'object' && 'status' in error && error.status === 'error') {
      return {
        success: false,
        message: getErrorMessage(error.error || 'unknown_error'),
        error: error.error,
        transactionId: error.transactionId
      };
    }

    return {
      success: false,
      message: 'Une erreur inconnue est survenue',
      error: 'unknown_error'
    };
  }
};

export const getErrorMessage = (error: string): string => {
  const errorMessages: { [key: string]: string } = {
    'app_not_installed': 'L\'application de paiement n\'est pas installée',
    'linking_failed': 'Impossible de lancer l\'application de paiement',
    'payment_failed': 'Le paiement a échoué',
    'invalid_amount': 'Montant invalide',
    'invalid_currency': 'Devise invalide',
    'invalid_callback': 'Réponse invalide de l\'application de paiement',
    'missing_data': 'Données manquantes',
    'timeout': 'Le paiement a expiré',
    'cancelled': 'Le paiement a été annulé',
    'network_error': 'Erreur de connexion',
    'unknown_error': 'Une erreur inconnue est survenue'
  };

  return errorMessages[error] || 'Une erreur est survenue lors du paiement';
};